'use client';

import React, {
  type ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Drawer,
  message,
  notification,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useConnection } from '@sezenta/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { useEvents } from '../../common/EventContext';
import { Utils } from '../../common/Utils';

export type CollectionReportEditModalInstance = {
  edit: (report: any) => void;
};

type Props = {
  onSuccess?: () => void;
};

type SiteOption = {
  label: string;
  value: string;
};

type ChangedCollectionRow = {
  quantity?: number;
  collectionType?: number;
  weight?: number;
  note?: string;
};

const CollectionReportEditModal: ForwardRefRenderFunction<
  CollectionReportEditModalInstance,
  Props
> = ({ onSuccess }, ref) => {
  const [report, setReport] = useState<any>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const connection = useConnection();
  const { sites, businessId, collectionType, select } = useBusinessContext();
  const events = useEvents();
  const [api, contextHolder] = notification.useNotification();
  const [selectedSite, setSelectedSite] = useState<SiteOption>();

  const siteOptions: SiteOption[] = useMemo(
    () => (select?.sites || []) as SiteOption[],
    [select?.sites],
  );

  useImperativeHandle(
    ref,
    () => ({
      edit: (r: any) => {
        setReport(r);
        form.setFieldsValue({
          ...r,
          createdAt: r.createdAt
            ? Utils.toDayJs(new Date(parseInt(r.createdAt)))
            : null,
          collections: r.collections || [],
          notes: r.notes || '',
        });
      },
    }),
    [form],
  );

  const handleCancel = () => {
    setReport(undefined);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const payload = {
        siteId: values.siteId,
        createdAt: values.createdAt
          ? values.createdAt.valueOf()
          : report.createdAt,
        collections: values.collections.map((collection: any) => ({
          quantity: collection.quantity,
          collectionType: collection.collectionType,
          weight: collection.weight,
          note: collection.note || '',
        })),
      };

      await connection.patch(`collection/${businessId}/${report.id}`, payload);
      events.emit('COLLECTION', {
        action: 'UPDATE',
        data: { ...report, ...payload },
      });
      setLoading(false);
      handleCancel();
      api.success({
        message: 'Collection Report',
        description: 'Collection Report has been updated successfully',
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Validation failed:', error);
      setLoading(false);
    }
  };
  const FilteredCollectionTypes = useMemo(() => {
    return Object.values(collectionType).filter(
      (c) => `${c?.state}` === report?.state,
    );
  }, [collectionType, report?.state]);

  // const handleChangeValues = useCallback(
  //   (changedValues: any, allValues: any) => {
  //     if (changedValues.collections) {
  //       const collections = allValues.collections;
  //       changedValues.collections.forEach(
  //         (changedCollection: any, index: number) => {
  //           if (changedCollection && changedCollection.quantity !== undefined) {
  //             const collectionIndex = FilteredCollectionTypes.findIndex(
  //               (v) => v.id === allValues.collections[index].collectionType,
  //             );
  //             if (collectionIndex >= 0) {
  //               const multiplier =
  //                 FilteredCollectionTypes[collectionIndex].weight ?? 0;
  //               const newWeight = changedCollection.quantity * multiplier;
  //               form.setFieldValue(['collections', index, 'weight'], newWeight);
  //             }
  //           }
  //         },
  //       );
  //     }
  //   },
  //   [FilteredCollectionTypes, form],
  // );

  const handleChangeValues = useCallback(
    (changedValues: any, allValues: any) => {
      const changedCollections = changedValues?.collections;
      const allCollections = allValues?.collections;

      if (!changedCollections || !Array.isArray(allCollections)) return;

      Object.entries(changedCollections).forEach(([indexStr, changedRow]) => {
        const index = Number(indexStr);
        const current = allCollections[index];
        if (!current) return;

        const row = changedRow as ChangedCollectionRow;

        const quantity =
          row.quantity !== undefined ? row.quantity : current.quantity;

        const typeId =
          row.collectionType !== undefined
            ? Number(row.collectionType)
            : Number(current.collectionType);

        if (quantity == null || typeId == null) return;

        const typeData = FilteredCollectionTypes.find(
          (c: any) => Number(c.id) === typeId,
        );

        if (!typeData) return;

        const newWeight = quantity * (typeData.weight ?? 0);
        form.setFieldValue(['collections', index, 'weight'], newWeight);
      });
    },
    [FilteredCollectionTypes, form],
  );

  return (
    <>
      <Drawer
        width={700}
        destroyOnClose
        title={
          <span className="text-xl font-bold text-gray-800 flex items-center">
            Edit Collection Report
          </span>
        }
        open={!!report}
        onClose={handleCancel}
        footer={
          <div className="flex justify-end">
            <Button
              key="submit"
              type="primary"
              disabled={loading}
              loading={loading}
              onClick={form.submit}
              icon={<SaveOutlined />}
            >
              Save Changes
            </Button>
          </div>
        }
        className="collection-report-modal"
      >
        {report && (
          <Form
            form={form}
            onFinish={handleSubmit}
            onValuesChange={handleChangeValues}
            layout="vertical"
            className="bg-gray-50 p-4 rounded-lg"
            initialValues={{
              ...report,
              createdAt: report.createdAt
                ? Utils.toDayJs(new Date(parseInt(report.createdAt)))
                : null,
              collections: report.collections || [],
            }}
          >
            {/*<div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 mb-4">*/}
            {/*  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-3">*/}
            {/*    Site Details*/}
            {/*  </h3>*/}
            {/*  <div className="flex">*/}
            {/*    <span className="text-gray-600 font-medium w-28">Site Name:</span>*/}
            {/*    <span className="text-gray-800">{report.siteName || 'N/A'}</span>*/}
            {/*  </div>*/}
            {/*</div>*/}

            <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-3">
                Report Details
              </h3>

              <Form.Item
                name="siteId"
                label="Select Site"
                rules={[{ required: true, message: 'Please select a site' }]}
              >
                <Select
                  placeholder="Choose a site"
                  options={siteOptions}
                  showSearch
                  optionFilterProp="label"
                  onChange={(value) => {
                    setSelectedSite(siteOptions.find((v) => v.value === value));
                  }}
                />
              </Form.Item>

              <Form.Item
                name="createdAt"
                label="Created Date & Time"
                className="mb-0"
              >
                <DatePicker
                  showTime
                  format="YYYY/MM/DD HH:mm"
                  placeholder="Select date and time"
                  className="w-full"
                />
              </Form.Item>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-3">
                Collections
              </h3>

              <Form.List name="collections">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div
                        key={key}
                        className="p-4 bg-gray-50 rounded-md border border-gray-200 mb-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-700">
                            Collection Item #{name + 1}
                          </h4>
                          <MinusCircleOutlined
                            className="text-red-500 text-xl cursor-pointer"
                            onClick={() => remove(name)}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Form.Item
                            {...restField}
                            name={[name, 'collectionType']}
                            label="Collection Type"
                            rules={[
                              {
                                required: true,
                                message: 'Please select collection type',
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select collection type"
                              options={FilteredCollectionTypes.map(
                                (type: any) => ({
                                  label: type.name,
                                  value: type.id,
                                }),
                              )}
                            />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, 'quantity']}
                            label="Quantity"
                            rules={[
                              {
                                required: true,
                                message: 'Please enter quantity',
                              },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              className="w-full"
                              placeholder="Quantity"
                            />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, 'weight']}
                            label="Weight (kg)"
                            dependencies={['quantity']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter weight',
                              },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              step={0.1}
                              className="w-full"
                              placeholder="Enter weight in kg"
                            />
                          </Form.Item>
                        </div>
                        <Form.Item
                          {...restField}
                          name={[name, 'note']}
                          label="Note"
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder="Enter notes for this collection item..."
                          />
                        </Form.Item>
                      </div>
                    ))}

                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            collectionType: undefined,
                            quantity: 0,
                            weight: 0,
                            note: '',
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Collection
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </div>
          </Form>
        )}
        {contextHolder}
      </Drawer>
    </>
  );
};

export const useCollectionReportEditModal = () =>
  useRef<CollectionReportEditModalInstance>(null);

export default React.forwardRef(CollectionReportEditModal);
